"use client";

import { useEffect, useRef, useState } from "react";
import { FolderOpen, Download, Upload, Trash2, Save, History } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, put, del, upload, can, downloadFile } from "@/utils/service";
import { apiMessage, shortDate } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, darkInput, darkLabel, darkPrimary, darkSecondary, darkDanger } from "@/components/crm/FloatingDrawer";
import ListShell from "@/components/crm/ListShell";
import SearchInput from "@/components/ui/search-input";

interface Doc {
  id: string; title: string; description: string | null; category: string | null; created_at: string;
  current_version: { id: string; version: number; original_name: string; mime_type: string; size: number; uploaded_at: string } | null;
  created_by: { name: string } | null; can: { update: boolean; delete: boolean };
}
interface Version { id: string; version: number; original_name: string; size: number; note: string | null; uploaded_by: { name: string } | null; uploaded_at: string }

const size = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.csv,.txt,.png,.jpg,.jpeg,.webp,.gif,.zip";

/**
 * Organization-wide documents (policies, templates, brand assets). Files attached to a specific
 * account, deal or contract follow that record's access rules.
 */
export default function DocumentsPage() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [selected, setSelected] = useState<Doc | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [meta, setMeta] = useState({ title: "", category: "", description: "", note: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await get<{ data: { documents: Doc[] } }>(`/documents${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setDocs(r.data.documents);
      setPage(1);
    } catch (e) {
      notify(apiMessage(e, "Could not load documents."));
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (d: Doc) => {
    setSelected(d);
    setMeta({ title: d.title, category: d.category ?? "", description: d.description ?? "", note: "" });
    const r = await get<{ data: { document: { versions: Version[] } } }>(`/documents/${d.id}`).catch(() => null);
    setVersions(r?.data.document.versions ?? []);
  };

  const submitUpload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      if (selected) {
        if (meta.note) fd.append("note", meta.note);
        await upload(`/documents/${selected.id}/versions`, fd);
        notify("New version uploaded");
        open(selected);
      } else {
        if (meta.title) fd.append("title", meta.title);
        if (meta.category) fd.append("category", meta.category);
        if (meta.description) fd.append("description", meta.description);
        await upload("/documents", fd);
        notify("Document uploaded");
        setUploadOpen(false);
      }
      setFile(null);
      load();
    } catch (e) {
      notify(apiMessage(e, "Could not upload the file. Allowed: PDF, Office files, images, CSV, TXT and ZIP."));
    } finally { setBusy(false); }
  };

  const saveMeta = async () => {
    if (!selected) return;
    try {
      await put(`/documents/${selected.id}`, { title: meta.title, category: meta.category || null, description: meta.description || null });
      notify("Saved");
      load();
    } catch (e) { notify(apiMessage(e, "Could not save.")); }
  };

  const remove = async () => {
    if (!selected || !await confirmDialog(`Delete "${selected.title}" and all its versions?`)) return;
    try { await del(`/documents/${selected.id}`); notify("Document deleted"); setSelected(null); load(); }
    catch (e) { notify(apiMessage(e, "Could not delete the document.")); }
  };

  const card = (d: Doc) => {
    const ext = (d.current_version?.original_name.split(".").pop() ?? "file").slice(0, 4).toUpperCase();
    const tone = ["PDF"].includes(ext) ? "bg-red-50 text-red-600" : ["DOC", "DOCX", "ODT", "TXT"].includes(ext) ? "bg-blue-50 text-blue-600"
      : ["XLS", "XLSX", "ODS", "CSV"].includes(ext) ? "bg-green-50 text-green-600" : ["PNG", "JPG", "JPEG", "WEBP", "GIF"].includes(ext) ? "bg-purple-50 text-purple-600" : "bg-gray-100 text-gray-600";
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-start gap-3">
          <span className={`flex h-12 w-10 shrink-0 items-center justify-center rounded-md text-[10px] font-bold ${tone}`}>{ext}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-gray-900">{d.title}</p>
            <p className="truncate text-xs text-gray-500">{d.current_version?.original_name ?? "No file"}</p>
          </div>
          <button aria-label="Download" onClick={(e) => { e.stopPropagation(); downloadFile(`/documents/${d.id}/download`, d.current_version?.original_name ?? d.title); }} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-primary"><Download className="h-4 w-4" /></button>
        </div>
        {d.category && <span className="mt-3 self-start rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{d.category}</span>}
        <div className="min-h-[1rem] flex-1" />
        <div className="flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
          <span>v{d.current_version?.version ?? 1}{d.current_version ? ` · ${size(d.current_version.size)}` : ""}</span>
          <span>{shortDate(d.current_version?.uploaded_at ?? d.created_at)}{d.created_by ? ` · ${d.created_by.name}` : ""}</span>
        </div>
      </div>
    );
  };

  const filePicker = (
    <div>
      <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <button type="button" onClick={() => fileRef.current?.click()} className="w-full rounded-lg border border-dashed border-gray-600 px-3 py-6 text-center text-xs text-gray-300 hover:border-primary">
        <Upload className="mx-auto mb-2 w-5 h-5" />{file ? `${file.name} (${size(file.size)})` : "Choose a file (max 20 MB)"}
      </button>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Documents" description="Shared files for the whole organization, with version history" />
        <ListShell
          renderCard={card}
          rowKey={(r) => r.id}
          rows={docs.slice((page - 1) * perPage, page * perPage)}
          pagination={{ page, per_page: perPage, total: docs.length, total_pages: Math.ceil(docs.length / perPage) }}
          onPage={setPage}
          onPerPage={(n) => { setPerPage(n); setPage(1); }}
          noun="documents"
          loading={loading}
          onRowClick={open}
          grid={{ id: "documents", search: <SearchInput className="w-full sm:w-64" placeholder="Search documents…" value={search} onChange={(e) => setSearch(e.target.value)} /> }}
          onAdd={can("documents.create") ? () => { setSelected(null); setFile(null); setMeta({ title: "", category: "", description: "", note: "" }); setUploadOpen(true); } : undefined}
          addLabel="Upload"
          empty={{ icon: FolderOpen, title: "No shared documents", text: "Upload templates, policies or brand files for everyone in the organization." }}
        />
      </div>

      <FloatingDrawer isOpen={uploadOpen} onClose={() => setUploadOpen(false)}>
        <DrawerHeader title="Upload Document" subtitle="Visible to everyone in the organization who can view documents" />
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {filePicker}
          <div><label className={darkLabel}>Title</label><input className={darkInput} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} placeholder="Defaults to the file name" /></div>
          <div><label className={darkLabel}>Category</label><input className={darkInput} value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} placeholder="e.g. template, policy, brand" /></div>
          <div><label className={darkLabel}>Description</label><textarea rows={3} className={`${darkInput} resize-none`} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} /></div>
        </div>
        <div className="p-6 border-t border-gray-800 space-y-3">
          <button onClick={submitUpload} disabled={busy || !file} className={darkPrimary}><Upload className="w-4 h-4" />{busy ? "Uploading..." : "Upload"}</button>
          <button onClick={() => setUploadOpen(false)} className={darkSecondary}>Cancel</button>
        </div>
      </FloatingDrawer>

      <FloatingDrawer isOpen={!!selected && !uploadOpen} onClose={() => setSelected(null)}>
        {selected && (
          <>
            <DrawerHeader title={selected.title} subtitle={selected.created_by ? `Added by ${selected.created_by.name}` : undefined} />
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <button onClick={() => downloadFile(`/documents/${selected.id}/download`, selected.current_version?.original_name ?? selected.title)} className={darkPrimary}><Download className="w-4 h-4" />Download latest</button>
              {selected.can.update && (
                <>
                  <div><label className={darkLabel}>Title</label><input className={darkInput} value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} /></div>
                  <div><label className={darkLabel}>Category</label><input className={darkInput} value={meta.category} onChange={(e) => setMeta({ ...meta, category: e.target.value })} /></div>
                  <div><label className={darkLabel}>Description</label><textarea rows={2} className={`${darkInput} resize-none`} value={meta.description} onChange={(e) => setMeta({ ...meta, description: e.target.value })} /></div>
                  <button onClick={saveMeta} className={darkSecondary}><Save className="inline w-4 h-4 mr-1" />Save details</button>
                  <div className="border-t border-gray-800 pt-4 space-y-2">
                    <p className={darkLabel}>Upload a new version</p>
                    {filePicker}
                    <input className={darkInput} placeholder="What changed? (optional)" value={meta.note} onChange={(e) => setMeta({ ...meta, note: e.target.value })} />
                    <button onClick={submitUpload} disabled={busy || !file} className={darkSecondary}>{busy ? "Uploading..." : "Upload version"}</button>
                  </div>
                </>
              )}
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-white"><History className="w-4 h-4" />Versions</p>
                {versions.map((v) => (
                  <div key={v.id} className="mb-1.5 flex items-center justify-between rounded-lg bg-gray-900 px-3 py-2 text-xs">
                    <span>v{v.version} · {v.original_name}<span className="block text-gray-400">{shortDate(v.uploaded_at)}{v.uploaded_by ? ` · ${v.uploaded_by.name}` : ""}{v.note ? ` · ${v.note}` : ""}</span></span>
                    <button aria-label="Download version" onClick={() => downloadFile(`/documents/${selected.id}/download/${v.id}`, v.original_name)} className="text-gray-400 hover:text-primary"><Download className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            {selected.can.delete && can("documents.delete") && (
              <div className="p-3 border-t border-gray-800"><button onClick={remove} className={darkDanger}><Trash2 className="w-4 h-4" />Delete</button></div>
            )}
          </>
        )}
      </FloatingDrawer>
    </div>
  );
}
